import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Receipt, Loader2, ChevronLeft, CreditCard, CheckCircle2, Clock, XCircle, Store, Crown, Star, MonitorSmartphone, GraduationCap, RotateCw } from "lucide-react";
import { api } from "../lib/api";

const money = (n, m = "PEN") => `${m === "PEN" ? "S/" : m} ${Number(n || 0).toFixed(2)}`;
const fecha = (iso) => (iso ? new Date(iso).toLocaleDateString("es-PE", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const REINTENTO_LINK = {
  Marketplace: "/tienda",
  Membresía: "/planes",
  Destacar: "/conexion/mis-publicaciones",
  Software: "/software",
  Academia: "/academia",
};

const ESTADO = {
  pagado: { label: "Pagado", color: "#16A34A", Icon: CheckCircle2 },
  pendiente: { label: "Pendiente", color: "#CA8A04", Icon: Clock },
  rechazado: { label: "Rechazado", color: "#DC2626", Icon: XCircle },
};
const ORIGEN_ICON = {
  Marketplace: Store, Membresía: Crown, Destacar: Star, Software: MonitorSmartphone, Academia: GraduationCap,
};

export default function PagosFacturacionPage() {
  const [data, setData] = useState(undefined);

  useEffect(() => {
    api.get("/cuenta/pagos").then(({ data }) => setData(data)).catch(() => setData(null));
  }, []);

  const r = data?.resumen;

  return (
    <div className="px-5 md:px-8 py-8 max-w-4xl" data-testid="pagos-facturacion-page">
      <Link to="/" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800">
        <ChevronLeft size={16} /> Volver al Ecosistema
      </Link>
      <h1 className="mt-4 text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight inline-flex items-center gap-3">
        <Receipt size={30} style={{ color: "#16A34A" }} /> Pagos y Facturación
      </h1>
      <p className="mt-2 text-slate-600">Historial de pagos, órdenes, suscripciones y comprobantes.</p>

      {r && (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-5">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Total pagado</p>
            <p className="mt-1 text-2xl font-extrabold text-emerald-600">{money(r.total_pagado, r.moneda)}</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-5">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Transacciones</p>
            <p className="mt-1 text-2xl font-extrabold text-slate-900">{r.transacciones}</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-5">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Pendientes</p>
            <p className="mt-1 text-2xl font-extrabold text-amber-600">{r.pendientes}</p>
          </div>
        </div>
      )}

      <div className="mt-6 bg-white rounded-2xl border border-slate-100 shadow-soft overflow-hidden">
        {data === undefined && <div className="flex items-center gap-2 text-slate-500 p-8"><Loader2 className="animate-spin" size={16} /> Cargando historial…</div>}
        {data === null && <p className="text-sm text-slate-400 p-8">No se pudo cargar el historial de pagos.</p>}
        {data && data.items.length === 0 && (
          <div className="text-center py-16" data-testid="pagos-vacio">
            <CreditCard size={40} className="mx-auto text-slate-300" />
            <p className="mt-3 text-slate-500">Aún no tienes pagos registrados.</p>
          </div>
        )}
        {data && data.items.length > 0 && (
          <div className="divide-y divide-slate-100">
            {data.items.map((p, i) => {
              const e = ESTADO[p.estado] || ESTADO.pendiente;
              const EIcon = e.Icon;
              const OIcon = ORIGEN_ICON[p.origen] || CreditCard;
              return (
                <div key={i} className="flex items-center gap-3 p-4" data-testid={`pago-item-${i}`}>
                  <span className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center flex-shrink-0 text-slate-500"><OIcon size={18} /></span>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-900 text-sm truncate">{p.concepto}</p>
                    <p className="text-xs text-slate-500">{p.origen} · {fecha(p.fecha)} · Ref: {String(p.referencia || "").slice(0, 10)}</p>
                  </div>
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ color: e.color, backgroundColor: `${e.color}15` }}>
                    <EIcon size={12} /> {e.label}
                  </span>
                  <span className="text-sm font-bold text-slate-900 w-24 text-right">{money(p.monto, p.moneda)}</span>
                  {p.estado === "pendiente" && (
                    <Link to={REINTENTO_LINK[p.origen] || "/"} data-testid={`pago-reintentar-${i}`}
                      className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg text-white hover:brightness-110 whitespace-nowrap" style={{ backgroundColor: "#16A34A" }}>
                      <RotateCw size={13} /> Completar pago
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <p className="mt-4 text-xs text-slate-400">Los comprobantes fiscales (boleta/factura) estarán disponibles próximamente. Por ahora puedes usar este historial como respaldo de tus pagos.</p>
    </div>
  );
}
